import { takeLatest, put, call, all } from 'redux-saga/effects';
import get from 'lodash/get';
import { enqueueSnackbarAction } from '../App/actions';
import { FESTIVALS, FESTIVAL_CONTACTS, VENUES } from '../../constants/endpointConstants';
import { create, deleteOne, updateOne } from '../../utils/API';
import {
  addAndUpdateFestivalContactsAction,
  updateFestivalInfoAction,
  updateFestivalVenueAction,
  getFestivalTranslationsAction,
  updateFestivalTranslationsAction,
  getFestivalReviewsAction,
  getFestivalProductionReviewsAction,
} from './actions';
import { i18n } from '../../src/i18n';
import { TP } from '../../constants/consts';
import * as API from '../../utils/API';

export function* addAndUpdateContacts({ payload, serverCookies }) {
  const callback = get(payload, 'callback');
  const endpoint = FESTIVAL_CONTACTS(payload?.id);
  try {
    const newContacts = get(payload, 'newContacts', []);
    const updatingContacts = get(payload, 'updatingContacts', []);
    const deletedContactsIds = get(payload, 'deletedContactsIds', []);

    yield all([
      ...(newContacts?.length > 0 ? [call(create, endpoint, { data: newContacts }, null, serverCookies)] : []),
      ...(updatingContacts?.length > 0
        ? [call(updateOne, endpoint, updatingContacts[0]?.id, { data: updatingContacts }, null, serverCookies)]
        : []),
      ...deletedContactsIds.map(deletedContactId => call(deleteOne, endpoint, deletedContactId, {}, serverCookies)),
    ]);

    if (callback && typeof callback === 'function') payload.callback(true);
    yield put(addAndUpdateFestivalContactsAction.success());
    yield put(
      enqueueSnackbarAction({
        message: i18n.t(`${TP}.FN_SUCCESS_UPDATING_CONTACTS`),
        options: {
          variant: 'success',
        },
      }),
    );
  } catch (err) {
    if (callback && typeof callback === 'function') payload.callback(false);
    yield put(addAndUpdateFestivalContactsAction.failure());
    yield put(
      enqueueSnackbarAction({
        message: i18n.t(`${TP}.FN_ERROR_UPDATING_CONTACTS`, {
          endpoint,
          error: err?.responseText ?? err?.messsage,
        }),
        options: {
          variant: 'error',
        },
      }),
    );
  }
}

export function* updateFestivalInfo({ payload, serverCookies }) {
  const callback = get(payload, 'callback');
  const id = get(payload, 'id');
  try {
    yield all([
      call(updateOne, FESTIVALS, id, payload?.info, null, serverCookies),
      call(addAndUpdateContacts, {
        payload: {
          id,
          newContacts: payload?.newContacts || [],
          updatingContacts: payload?.updatedContacts || [],
          deletedContactsIds: payload?.deletedContacts || [],
        },
      }),
    ]);

    if (callback && typeof callback === 'function') payload.callback(true);
  } catch (err) {
    if (callback && typeof callback === 'function') payload.callback(false);
    yield put(
      enqueueSnackbarAction({
        message: i18n.t(`${TP}.FN_ERROR_UPDATING_FESTIVAL_INFO`, { error: err?.messsage }),
        options: {
          variant: 'error',
        },
      }),
    );
  }
}

export function* updateFestivalVenue({ payload, serverCookies }) {
  const callback = get(payload, 'callback');
  const venues = get(payload, 'venues', []);
  try {
    yield all(venues?.map(v => call(updateOne, VENUES, v?.id, v, null, serverCookies)));
    if (callback && typeof callback === 'function') {
      payload.callback();
    }
    yield put(
      enqueueSnackbarAction({
        message: i18n.t(`${TP}.FN_SUCCESS_UPDATING_VENUE`),
        options: {
          variant: 'success',
        },
      }),
    );
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message: i18n.t(`${TP}.FN_ERROR_UPDATING_VENUE`, { error: err?.messsage }),
        options: {
          variant: 'error',
        },
      }),
    );
  }
}

export function* updateFestivalTranslations({ payload, serverCookies }) {
  const callback = get(payload, 'callback');
  const festivalId = get(payload, 'festivalId');
  try {
    const translations = yield call(
      API.updateFestivalTranslations,
      festivalId,
      payload.festivalTranslations,
      serverCookies,
    );
    yield put(getFestivalTranslationsAction.success(get(translations, 'data')));

    if (callback && typeof callback === 'function') payload.callback(true);
  } catch (err) {
    if (callback && typeof callback === 'function') payload.callback(false);
    yield put(
      enqueueSnackbarAction({
        message: `Error updating festival name: ${err?.message}`,
        options: {
          variant: 'error',
        },
      }),
    );
  }
}

export function* fetchFestivalReviews({ payload, serverCookies }) {
  const { url, pagination, query } = payload;
  try {
    const response = yield call(API.getReviewsV1, url, pagination, query ?? {}, serverCookies);
    yield put(getFestivalReviewsAction.success(get(response, 'data')));
  } catch (err) {
    yield put(getFestivalReviewsAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: err?.message,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(getFestivalReviewsAction.fulfill());
  }
}

export function* fetchFestivalProductionReviews({ payload, serverCookies }) {
  const { url, pagination, query } = payload;
  try {
    const response = yield call(API.getReviewsV1, url, pagination, query ?? {}, serverCookies);
    yield put(getFestivalProductionReviewsAction.success(get(response, 'data')));
  } catch (err) {
    yield put(getFestivalProductionReviewsAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: err?.message,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(getFestivalProductionReviewsAction.fulfill());
  }
}

export default function* festivalSaga() {
  yield takeLatest(addAndUpdateFestivalContactsAction.TRIGGER, addAndUpdateContacts);
  yield takeLatest(updateFestivalInfoAction.TRIGGER, updateFestivalInfo);
  yield takeLatest(updateFestivalVenueAction.TRIGGER, updateFestivalVenue);
  yield takeLatest(updateFestivalTranslationsAction.TRIGGER, updateFestivalTranslations);
  yield takeLatest(getFestivalReviewsAction.TRIGGER, fetchFestivalReviews);
  yield takeLatest(getFestivalProductionReviewsAction.TRIGGER, fetchFestivalProductionReviews);
}
